export type UserRole = "employee" | "manager" | "admin" | "superadmin";

export interface UserAccess {
  role: UserRole;
  isAdmin: boolean;
  isManager: boolean;
  isSuperAdmin: boolean;
  canApproveLeaves: boolean;
  canApproveLeaveLevel1: boolean;
  canApproveLeaveLevel2: boolean;
  canViewAllEmployees: boolean;
  canViewTeam: boolean;
  canEditAdminFields: boolean;
  canManageRoles: boolean;
  canManageConfig: boolean;
  canManageEmployees: boolean;
  canApproveAttendanceCorrections: boolean;
  canViewManagerDashboard: boolean;
  canViewAdminDashboard: boolean;
  canViewReports: boolean;
}

/** HRMS records are keyed by email: always trim + lowercase before use. */
export const normalizeEmail = (email: string | null | undefined): string =>
  (email || "").trim().toLowerCase();

/**
 * Sessions created before login normalized the email may still hold
 * " name@manoshala.com" in localStorage; fix it once at startup so those
 * users don't have to log in again.
 */
export const normalizeStoredEmail = (): void => {
  try {
    const stored = localStorage.getItem("logged_in_email");
    if (stored && stored !== normalizeEmail(stored)) {
      localStorage.setItem("logged_in_email", normalizeEmail(stored));
    }
  } catch {
    // storage unavailable; nothing to fix
  }
};

const getStoredPermissions = (): string[] => {
  try {
    const raw = localStorage.getItem("logged_in_user_hrms_permissions");
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * RBAC (Role Management): checks the fine-grained permission set resolved by
 * the backend at login (`hrmsPermissions`, stored under
 * `logged_in_user_hrms_permissions`) — distinct from the legacy tab-based
 * `logged_in_user_permissions` list and `logged_in_user_accesscontrols`.
 */
export const hasPermission = (permissionKey: string): boolean => {
  return getStoredPermissions().includes(permissionKey);
};

/**
 * Get user access permissions for HRMS.
 * Role tier is derived from the backend's hrmsPermissions array (see the
 * role/permission table in docs/superpowers/specs/2026-09-17-hrms-rbac-design.md
 * in the experts-db repo), not a separate role-name field:
 *  - Super Admin: has "roles.manage"
 *  - Admin:       has "employees.manage" (and not roles.manage)
 *  - Manager:     has "attendance.view.team" (and neither of the above)
 *  - Employee:    none of the above
 */
export const getUserAccess = (): UserAccess => {
  const has = hasPermission;

  const isSuperAdmin = has("roles.manage");
  const isAdminTier = isSuperAdmin || has("employees.manage");
  const isManager = !isAdminTier && has("attendance.view.team");
  const role: UserRole = isSuperAdmin
    ? "superadmin"
    : isAdminTier
      ? "admin"
      : isManager
        ? "manager"
        : "employee";

  const canApproveLeaveLevel1 = has("leave.approve.level1");
  const canApproveLeaveLevel2 = has("leave.approve.level2");

  return {
    role,
    isAdmin: isAdminTier,
    isManager,
    isSuperAdmin,
    canApproveLeaves: canApproveLeaveLevel1 || canApproveLeaveLevel2,
    canApproveLeaveLevel1,
    canApproveLeaveLevel2,
    canViewAllEmployees: has("attendance.view.all"),
    canViewTeam: has("attendance.view.team"),
    canEditAdminFields: isAdminTier,
    canManageRoles: has("roles.manage"),
    canManageConfig: has("config.manage"),
    canManageEmployees: has("employees.manage"),
    canApproveAttendanceCorrections: has("attendance.correct.approve"),
    canViewManagerDashboard: has("dashboard.manager.view"),
    canViewAdminDashboard: has("dashboard.admin.view"),
    canViewReports: has("reports.view"),
  };
};

export const isUserAdmin = (): boolean => {
  return getUserAccess().isAdmin;
};

export const canUserApproveLeaves = (): boolean => {
  return getUserAccess().canApproveLeaves;
};

export const canUserViewAllEmployees = (): boolean => {
  return getUserAccess().canViewAllEmployees;
};

export const canUserViewTeam = (): boolean => {
  return getUserAccess().canViewTeam;
};
